import { createAction } from "redux-actions";
import { makeActionType } from "../utils";
import { resetError, setError } from "./errors";
import request from './utils/request';
import { urls } from "../constants/app";
import { calculateRawComing, calculateRawExpenditure, calculateRawWriteoff, getOrdersWithReceipes, getWriteoffsWithReceipes } from "./recipes";
import { isEmpty } from "lodash";
import moment from "moment";

const namespace = 'INVENTORY_FORMS';

export const setInventoryForms = createAction(makeActionType(namespace, ['SET']));
export const setInventoryFormsCount = createAction(makeActionType(namespace, ['SET', 'COUNT']));
export const setInventoryForm = createAction(makeActionType(namespace, ['SET', 'INVENTORY_FORM']));
export const deleteInventoryFormSuccess = createAction(makeActionType(namespace, ['DELETE', 'INVENTORY_FORM']));
export const addInventory = createAction(makeActionType(namespace, ['ADD']));
export const setPagingInventory = createAction(makeActionType(namespace, ['SET', 'PAGING']));
export const setInventoryFilters = createAction(makeActionType(namespace, ['SET', 'FILTERS']));

export function fetchInventoryForms(withoutLoader = false) {
    return async (dispatch, getState) => {
        if (!withoutLoader) dispatch({ type: 'SET LOADER', field: 'inventoryForms', value: true });
        dispatch(resetError('inventoryForms'));
        const paging = getState().inventoryForms.paging;
        const filters = getState().inventoryForms.filters;

        try {
            const user = getState().user.info;
            const params = { _sort: 'DATE:DESC' };
            const dataParams = {};

            if (filters.point && filters.point.value) {
                dataParams.point = filters.point.value;
                params.point = filters.point.value;
            } else if (user.points.length > 0) {
                const pointIds = user.points.map(item => item.id);
                dataParams['point.id_in'] = pointIds;
                params['point.id_in'] = pointIds;
            }
            
            params['_start'] = paging.start;
            params['_limit'] = paging.limit;
            const count = await request({ method: 'get', url: `${urls.inventoryforms}/count` , params: dataParams});
            dispatch(setInventoryFormsCount(count));
            const inventoryFormsData = await request({ method: 'get', url: urls.inventoryforms, params });
            dispatch(setInventoryForms(inventoryFormsData));
        } catch (error) {
            dispatch(setError(error, 'inventoryForms'));
        } finally {
            if (!withoutLoader) {
                dispatch({ type: 'SET LOADER', field: 'inventoryForms', value: false });
            }
        }
    }
}

export function fetchInventoryForm(id, force = false) {
    return async (dispatch, getState) => {
        const state = getState().inventoryForms;

        if (!force && (state.formsById[id] || state.loadingById[id])) {
            return;
        }

        dispatch({ type: 'SET LOADER', field: 'inventoryForm', value: true, id });
        dispatch(resetError('inventoryForm'));

        try {
            const inventoryForm = await request({ method: 'get', url: `${urls.inventoryforms}/${id}` });
            dispatch(setInventoryForm(inventoryForm));
            return inventoryForm;
        } catch (error) {
            dispatch(setError(error, 'inventoryForm'));
            return {error: error};
        } finally {
            dispatch({ type: 'SET LOADER', field: 'inventoryForm', value: false, id });
        }
    };
}

export const getDataListInventory = async (params, requestData) => {
    const _limit = 400;
    let hasMore = true;
    let result = [];
    let lastId = null;

    try {
        while (hasMore) {
            const requestParams = {
                method: requestData.method, 
                url: requestData.url, 
            }

            if (requestData.method === 'post') {
                requestParams.data = { ...params, lastId, _limit };
            } else {
                requestParams.params = { ...params, lastId, _limit };
            }

            const response = await request(requestParams);
            
            if (!Array.isArray(response) || response.length === 0) {
                hasMore = false;
                break;
            } else {
                result = [...result, ...response];
                lastId = response[response.length - 1].id;
                
                const delay = response.length === _limit ? 50 : 0;
                if (delay > 0) {
                    await new Promise(resolve => setTimeout(resolve, delay));
                } else {
                    hasMore = false;
                }
            }
        }
        return result;
    } catch (error) {
        return {error: error};
    }
}

export function fetchRawsListInventoryForm(id) {
    return async () => {
        try {
            const params = { inventoryform: id, _sort: 'id:desc' }
            const raws = await getDataListInventory(params, {method: 'post', url: `${urls.rawmaterialsinventories}/listInventory`});
            return raws;
        } catch (error) {
            return { error: error.data }
        }
    }
}

export function fetchRawsInventoryForm(params) {
    return async () => {
        try {
            const raws = await request({ method: 'get', url: urls.rawmaterialsinventories, params})
            return raws;
        } catch (error) {
            return { error: error.data }
        }
    }
}

const getInventoryData = (data, lastInventory, receipes) => {
    return async (dispatch) => {
        const params = {
            point: data.point,
            date_lte: data.date,
            date_gt: lastInventory.date,
            _sort: 'id:desc',
        };

        const invoices = await getDataListInventory(params, {method: 'post', url: `${urls.invoiceforms}/listInventory`});
        if (invoices.error) return invoices;
        const writeoffs = await getDataListInventory(params, {method: 'post', url: `${urls.writeoffs}/listInventory`});
        if (writeoffs.error) return writeoffs;

        const paramsOrders = {
            pointsId: [data.point],
            created_at_lte: moment(data.date).format('YYYY-MM-DD HH:mm:ss'),
            created_at_gt: moment(lastInventory.date).format('YYYY-MM-DD HH:mm:ss'),
        };

        const orders = await getDataListInventory(paramsOrders, {method: 'post', url: `${urls.orders}/listInventory`});
        if (orders.error) return orders;
        const archive = await getDataListInventory(paramsOrders, {method: 'post', url: `${urls.archive}/listInventory`});
        if (archive.error) return archive;
        const ordersData = [...orders, ...archive];

        const ordersWithReceipes = await dispatch(getOrdersWithReceipes(ordersData, receipes));
        const writeoffsWithReceipes = await dispatch(getWriteoffsWithReceipes(writeoffs, receipes))

        const rawMaterials = [];
        for (const item of data.rawMaterials) {
            const raw = { ...item };
            raw.coming = calculateRawComing(invoices, raw.id);
            raw.expenditure = calculateRawExpenditure(ordersWithReceipes, raw.id);
            raw.writeoff = calculateRawWriteoff(writeoffsWithReceipes, raw.id);
        
            if (lastInventory && lastInventory.id) {
                const rawParams = { rawmaterial: raw.id, inventoryform: lastInventory.id };
                const prevInventory = await request({ method: 'post', url: `${urls.rawmaterialsinventories}/prevInventory`, data: rawParams });
                raw.prevInventory = prevInventory ? prevInventory.quantity || 0 : 0;
            }
            rawMaterials.push(raw);
        }

        return { rawMaterials, date: data.date, point: data.point, changeLog: data.changeLog || {} };
    };
};

export function createInventory(data) {
    return async (dispatch) => {
        dispatch({ type: 'SET LOADER', field: 'inventoryForm', value: true });
        dispatch({ type: 'SET SAVING', field: 'inventoryForm', value: true });
        dispatch(resetError('inventoryForm'));
        
        try {
            const dataParams = { point: data.point };
            const lastInventory = await request({ method: 'post', url: `${urls.inventoryforms}/getLast`, data: dataParams });
            let newInventory = null;
            
            if (!lastInventory) {
                newInventory = await request({ method: 'post', url: urls.inventoryforms, data });
            } else {
                if (lastInventory.date > data.date) return { error: { message: `Невозможно создать инвентаризацию раньше последней существующей (${moment(lastInventory.date).format('DD.MM.YYYY HH:mm')})` }};
                
                const recipes = await request({ method: 'get', url: urls.recipes, params: { _limit: -1 } });
                dispatch({ type: 'SET_RECEIPES', list: recipes });
                
                const inventoryData = await dispatch(getInventoryData(data, lastInventory, recipes));
                if (inventoryData.error) {
                    let error = inventoryData.error;
                    let text = '';
                    if (error.data && (error.message || error.statusCode)) text = `${error.statusCode || ''} ${error.message || ''}`;
                    return { error: { message: `Ошибка создания инвентаризации. ${text}` }};
                }
                
                newInventory = await request({ method: 'post', url: urls.inventoryforms, data: inventoryData });
            }
            
            if (newInventory) dispatch(addInventory(newInventory));            
            dispatch({ type: 'SET LOADER', field: 'inventoryForms', value: false });
            dispatch({ type: 'SET SAVING', field: 'inventoryForm', value: false });
            return true;
        } catch (error) {
            dispatch(setError(error, 'inventoryForm'));
            dispatch({ type: 'SET LOADER', field: 'inventoryForms', value: false });
            dispatch({ type: 'SET SAVING', field: 'inventoryForm', value: false });
            return false;
        }
    };
}

export function updateInventory(id, data) {
    return async (dispatch) => {
        dispatch({ type: 'SET LOADER', field: 'inventoryForm', value: true });
        dispatch({ type: 'SET SAVING', field: 'inventoryForm', value: true });
        dispatch(resetError('inventoryForm'));
        
        try {
            const dataParams = { point: data.point, id_ne: id };
            const lastInventory = await request({ method: 'post', url: `${urls.inventoryforms}/getLast`, data: dataParams });
            let newInventory = null;
            
            if (!lastInventory || isEmpty(lastInventory)) {
                newInventory = await request({ method: 'put', url: `${urls.inventoryforms}/${id}`, data });
            } else {
                if (lastInventory.date > data.date) return { error: { message: `Невозможно создать инвентаризацию раньше последней существующей (${moment(lastInventory.date).format('DD.MM.YYYY HH:mm')})` }};
                
                const recipes = await request({ method: 'get', url: urls.recipes, params: { _limit: -1 } });
                dispatch({ type: 'SET_RECEIPES', list: recipes });
                
                const inventoryData = await dispatch(getInventoryData(data, lastInventory, recipes));
                if (inventoryData.error) {
                    let error = inventoryData.error;
                    let text = '';
                    if (error.data && (error.message || error.statusCode)) text = `${error.statusCode || ''} ${error.message || ''}`;
                    return { error: { message: `Ошибка обновления инвентаризации. ${text}` }};
                }
                
                newInventory = await request({ method: 'put', url: `${urls.inventoryforms}/${id}`, data: inventoryData });
            }
            
            if (newInventory) dispatch(setInventoryForm(newInventory));            
            dispatch({ type: 'SET LOADER', field: 'inventoryForms', value: false });
            dispatch({ type: 'SET SAVING', field: 'inventoryForm', value: false });
            return true;
        } catch (error) {
            dispatch(setError(error, 'inventoryForm'));
            dispatch({ type: 'SET LOADER', field: 'inventoryForms', value: false });
            dispatch({ type: 'SET SAVING', field: 'inventoryForm', value: false });
            return false;
        }
    };
}

export function recalculationInventories(ids) {
    return async (dispatch) => {
        dispatch({ type: 'SET LOADER', field: 'inventoryForms', value: true });
        dispatch(resetError('inventoryForms'));

        try {
            await request({ method: 'post', url: `${urls.inventoryforms}/recalculationInventories`, data: { ids } });
            dispatch({ type: 'SET LOADER', field: 'inventoryForms', value: false });
        } catch (error) {
            dispatch({ type: 'SET LOADER', field: 'inventoryForms', value: false });
            return error;
        }
    }
}

export function recalculationInventory(id, data) {
    return async (dispatch) => {
        dispatch({ type: 'SET LOADER', field: 'inventoryForm', value: true });
        dispatch({ type: 'SET SAVING', field: 'inventoryForm', value: true });
        dispatch(resetError('inventoryForm'));
        
        try {
            const dataParams = { point: data.point, id_ne: id };
            dataParams.date_gt = data.date;
            const lastInventory = await request({ method: 'post', url: `${urls.inventoryforms}/getLast`, data: dataParams });
            let newInventory = null;
            
            if (!lastInventory || isEmpty(lastInventory)) {
                newInventory = await request({ method: 'put', url: `${urls.inventoryforms}/${id}`, data });
            } else {
                if (lastInventory.date > data.date) return { error: { message: `Невозможно создать инвентаризацию раньше последней существующей (${moment(lastInventory.date).format('DD.MM.YYYY HH:mm')})` }};
                
                const recipes = await request({ method: 'get', url: urls.recipes, params: { _limit: -1 } });
                dispatch({ type: 'SET_RECEIPES', list: recipes });
                
                const inventoryData = await dispatch(getInventoryData(data, lastInventory, recipes));
                if (inventoryData.error) {
                    let error = inventoryData.error;
                    let text = '';
                    if (error.data && (error.message || error.statusCode)) text = `${error.statusCode || ''} ${error.message || ''}`;
                    return { error: { message: `Ошибка обновления инвентаризации. ${text}` }};
                }
                
                newInventory = await request({ method: 'put', url: `${urls.inventoryforms}/${id}`, data: inventoryData });
            }
            
            if (newInventory) dispatch(setInventoryForm(newInventory));            
            dispatch({ type: 'SET LOADER', field: 'inventoryForm', value: false });
            dispatch({ type: 'SET SAVING', field: 'inventoryForm', value: false });
            return true;
        } catch (error) {
            dispatch(setError(error, 'inventoryForm'));
            dispatch({ type: 'SET LOADER', field: 'inventoryForm', value: false });
            dispatch({ type: 'SET SAVING', field: 'inventoryForm', value: false });
            return false;
        }
    };
}

export function deleteInventoryForm(id) {
    return async (dispatch) => {
        dispatch({ type: 'SET LOADER', field: 'inventoryForm', value: true });
        dispatch(resetError('inventoryForm'));
        try {
            await request({ method: 'delete', url: `${urls.inventoryforms}/${id}` })
            dispatch(deleteInventoryFormSuccess(id));
        } catch (error) {
            dispatch(setError(error, 'inventoryForms'));
        }
    }
}

export function getLastInventory(point, id_ne) {
    return async () => {
        if (!point || !point.id) return { error: { message: 'Нет данных о филиале.' }}
        const data = { point: point.id }
        if (id_ne) data['id_ne'] = id_ne;

        try {
            const lastInventory = await request({ method: 'post', url: `${urls.inventoryforms}/getLast`, data });
            return lastInventory;        
        } catch (error) {
            return { error: error.data };
        }
    }
    
}

